if (typeof OfferingsFilteredGrid === "undefined") {
  class OfferingsFilteredGrid extends HTMLElement {
    constructor() {
      super();
      this.isLoading = false;
      this.boundClick = this.onClick.bind(this);
      this.boundKeydown = this.onKeydown.bind(this);
      this.boundPopState = this.onPopState.bind(this);
    }

    connectedCallback() {
      this.addEventListener("click", this.boundClick);
      this.addEventListener("keydown", this.boundKeydown);
      window.addEventListener("popstate", this.boundPopState);
      this.syncActiveFilterFromDom();
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.boundClick);
      this.removeEventListener("keydown", this.boundKeydown);
      window.removeEventListener("popstate", this.boundPopState);
    }

    get sectionId() {
      return this.dataset.sectionId || "";
    }

    get filterParam() {
      return this.dataset.filterParam || "tag";
    }

    get currentPage() {
      return parseInt(this.dataset.currentPage, 10) || 1;
    }

    set currentPage(value) {
      this.dataset.currentPage = String(value);
    }

    get totalPages() {
      return parseInt(this.dataset.totalPages, 10) || 1;
    }

    set totalPages(value) {
      this.dataset.totalPages = String(value);
    }

    get hasMore() {
      return this.dataset.hasMore === "true";
    }

    set hasMore(value) {
      this.dataset.hasMore = value ? "true" : "false";
    }

    get activeFilter() {
      return this.dataset.filter || "all";
    }

    set activeFilter(value) {
      this.dataset.filter = value || "all";
    }

    get grid() {
      return this.querySelector("[data-offerings-grid]");
    }

    get loadMore() {
      return this.querySelector("[data-offerings-load-more]");
    }

    prefersReducedMotion() {
      return (
        window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false
      );
    }

    /**
     * Run a DOM update inside a View Transition when supported.
     * Fetch happens before this so the transition only covers the swap.
     */
    async runViewTransition(update) {
      if (
        typeof document.startViewTransition !== "function" ||
        this.prefersReducedMotion()
      ) {
        await update();
        return;
      }

      try {
        const transition = document.startViewTransition(async () => {
          await update();
        });
        await transition.finished;
      } catch {
        // Transition skipped/aborted — DOM update already attempted inside callback
      }
    }

    onClick(event) {
      const filterBtn = event.target.closest("[data-offerings-filter]");
      if (filterBtn && this.contains(filterBtn)) {
        event.preventDefault();
        const nextFilter = filterBtn.getAttribute("data-offerings-filter") || "all";
        if (nextFilter === this.activeFilter || this.isLoading) return;
        this.applyFilter(nextFilter);
        return;
      }

      const loadMore = event.target.closest("[data-offerings-load-more]");
      if (loadMore && this.contains(loadMore)) {
        event.preventDefault();
        this.loadMoreItems();
      }
    }

    onKeydown(event) {
      if (event.key !== "Enter" && event.key !== " ") return;

      const filterBtn = event.target.closest("[data-offerings-filter]");
      if (filterBtn && this.contains(filterBtn)) {
        event.preventDefault();
        const nextFilter = filterBtn.getAttribute("data-offerings-filter") || "all";
        if (nextFilter === this.activeFilter || this.isLoading) return;
        this.applyFilter(nextFilter);
        return;
      }

      const loadMore = event.target.closest("[data-offerings-load-more]");
      if (loadMore && this.contains(loadMore)) {
        event.preventDefault();
        this.loadMoreItems();
      }
    }

    onPopState() {
      const params = new URLSearchParams(window.location.search);
      const nextFilter = params.get(this.filterParam) || "all";
      this.fetchAndReplace({ filter: nextFilter, page: 1, pushState: false });
    }

    syncActiveFilterFromDom() {
      const active = this.activeFilter;
      for (const btn of this.querySelectorAll("[data-offerings-filter]")) {
        const isActive = (btn.getAttribute("data-offerings-filter") || "all") === active;
        btn.setAttribute("aria-pressed", isActive ? "true" : "false");
        btn.classList.toggle("is-active", isActive);
      }
      this.updateLoadMoreState();
    }

    /** Browser-visible URL: `tag` only (no `page`). */
    buildPublicUrl(filter) {
      const url = new URL(window.location.pathname, window.location.origin);
      if (filter && filter !== "all") {
        url.searchParams.set(this.filterParam, filter);
      }
      return url;
    }

    /** Section fetch: filter + page + section_id so Liquid `current_page` works. */
    buildSectionUrl({ filter, page }) {
      const url = new URL(window.location.pathname, window.location.origin);
      if (filter && filter !== "all") {
        url.searchParams.set(this.filterParam, filter);
      }
      if (page && page > 1) {
        url.searchParams.set("page", String(page));
      }
      if (this.sectionId) {
        url.searchParams.set("section_id", this.sectionId);
      }
      return url;
    }

    applyFilter(filter) {
      const publicUrl = this.buildPublicUrl(filter);
      history.pushState({ offeringsFilter: filter }, "", publicUrl.toString());
      this.fetchAndReplace({ filter, page: 1, pushState: false });
    }

    async loadMoreItems() {
      if (this.isLoading || !this.hasMore) return;
      await this.fetchAndAppend(this.currentPage + 1);
    }

    async fetchAndReplace({ filter, page, pushState }) {
      if (this.isLoading || !this.sectionId) return;

      this.isLoading = true;
      this.setAttribute("aria-busy", "true");
      this.updateLoadMoreState();

      try {
        const publicUrl = this.buildPublicUrl(filter);
        const response = await fetch(
          this.buildSectionUrl({ filter, page }).toString(),
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const next =
          doc.querySelector("offerings-filtered-grid") ||
          doc.querySelector(`[data-section-id="${this.sectionId}"]`);

        if (!next) {
          throw new Error("Offerings section markup missing from response");
        }

        next.dataset.filter = filter || "all";

        await this.runViewTransition(() => {
          this.replaceWith(next);
        });

        if (pushState) {
          history.pushState(
            { offeringsFilter: filter },
            "",
            publicUrl.toString(),
          );
        }
      } catch (error) {
        console.error("Offerings filter error:", error);
        this.isLoading = false;
        this.setAttribute("aria-busy", "false");
        this.updateLoadMoreState(true);
      }
    }

    async fetchAndAppend(page) {
      if (this.isLoading || !this.sectionId) return;

      this.isLoading = true;
      this.setAttribute("aria-busy", "true");
      this.updateLoadMoreState();

      try {
        const response = await fetch(
          this.buildSectionUrl({
            filter: this.activeFilter,
            page,
          }).toString(),
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const nextRoot =
          doc.querySelector("offerings-filtered-grid") ||
          doc.querySelector(`[data-section-id="${this.sectionId}"]`);
        const nextGrid = nextRoot?.querySelector("[data-offerings-grid]");
        const grid = this.grid;

        if (!nextGrid || !grid) {
          throw new Error("Offerings grid markup missing from response");
        }

        const existingHandles = new Set(
          Array.from(grid.querySelectorAll("[data-offering-card]")).map((card) =>
            card.getAttribute("data-handle"),
          ),
        );

        const cards = Array.from(nextGrid.querySelectorAll("[data-offering-card]"));
        const toAppend = [];

        for (const card of cards) {
          const handle = card.getAttribute("data-handle");
          if (handle && existingHandles.has(handle)) continue;
          toAppend.push(card.cloneNode(true));
          if (handle) existingHandles.add(handle);
        }

        await this.runViewTransition(() => {
          for (const card of toAppend) {
            grid.appendChild(card);
          }
        });

        this.currentPage = page;
        if (nextRoot) {
          this.totalPages =
            parseInt(nextRoot.getAttribute("data-total-pages"), 10) || this.totalPages;
          this.hasMore = nextRoot.getAttribute("data-has-more") === "true";
        }

        if (this.currentPage >= this.totalPages) {
          this.hasMore = false;
        }

        this.dispatchEvent(
          new CustomEvent("offerings-filtered-grid:loaded", {
            detail: {
              page: this.currentPage,
              added: toAppend.length,
            },
            bubbles: true,
          }),
        );

        if (!this.hasMore) {
          this.loadMore?.remove();
          this.dispatchEvent(
            new CustomEvent("offerings-filtered-grid:complete", {
              detail: {
                page: this.currentPage,
                totalPages: this.totalPages,
              },
              bubbles: true,
            }),
          );
        }
      } catch (error) {
        console.error("Offerings load more error:", error);
        this.updateLoadMoreState(true);
      } finally {
        this.isLoading = false;
        this.setAttribute("aria-busy", "false");
        this.updateLoadMoreState();
      }
    }

    updateLoadMoreState(isError = false) {
      const loadMore = this.loadMore;
      if (!loadMore) return;

      const defaultLabel =
        loadMore.dataset.defaultLabel ||
        loadMore.querySelector("[data-load-more-label]")?.textContent?.trim() ||
        "More offerings";

      loadMore.hidden = !this.hasMore;
      loadMore.setAttribute("aria-busy", this.isLoading ? "true" : "false");
      loadMore.classList.toggle("is-loading", this.isLoading);
      loadMore.classList.toggle("is-error", isError);
      loadMore.classList.toggle("pointer-events-none", this.isLoading);
      loadMore.classList.toggle("opacity-60", this.isLoading);

      const label = loadMore.querySelector("[data-load-more-label]");
      if (label) {
        label.textContent = this.isLoading ? "Loading..." : defaultLabel;
      }
    }
  }

  if (!customElements.get("offerings-filtered-grid")) {
    customElements.define("offerings-filtered-grid", OfferingsFilteredGrid);
  }
}
